import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthenticatedFetch } from './useAuthenticatedFetch';
import { useLanguage } from '../providers/LanguageProvider';
import type { ApiResponse, ReviewPromptOutcome, ReviewPromptStatus } from '../types';

/**
 * Ask the merchant to review Aisthetix on the App Store, at the moment the
 * product promise has landed for them (revenue attributed to try-on above zero).
 *
 * The server owns the trigger and the frequency caps; this hook only renders the
 * decision and reports back what the merchant chose. `source` names the surface
 * raising the prompt, so the same hook can be reused on any other legitimate
 * moment with no change to the caps.
 *
 * Every path that does not end with the review modal on screen is recorded as
 * 'unavailable', which closes the prompt for this shop just as a dismissal does:
 * the shop's one ask has been spent either way, so nobody gets asked twice.
 */
export function useReviewPrompt(source: string) {
  const authenticatedFetch = useAuthenticatedFetch();
  const { t } = useLanguage();
  const [visible, setVisible] = useState(false);
  const checked = useRef(false);

  const report = useCallback(
    (outcome: ReviewPromptOutcome) => {
      authenticatedFetch('/api/review/prompt/outcome', {
        method: 'POST',
        body: JSON.stringify({ outcome, source }),
      }).catch((err) => console.warn('[Review] outcome not recorded:', err));
    },
    [authenticatedFetch, source]
  );

  // Ask the server whether this shop is due a prompt. Never blocks the page:
  // any failure simply leaves the prompt hidden.
  useEffect(() => {
    if (checked.current) return; // one eligibility check per mount
    checked.current = true;

    let cancelled = false;

    authenticatedFetch('/api/review/prompt')
      .then((res) => res.json() as Promise<ApiResponse<ReviewPromptStatus>>)
      .then((json) => {
        if (cancelled || !json?.success || !json.data?.eligible) return;
        setVisible(true);
        // Recorded on render: showing the prompt is what spends the one ask.
        return authenticatedFetch('/api/review/prompt/shown', {
          method: 'POST',
          body: JSON.stringify({ source }),
        }).then(() => undefined);
      })
      .catch((err) => console.warn('[Review] eligibility check failed:', err));

    return () => {
      cancelled = true;
    };
  }, [authenticatedFetch, source]);

  const leaveReview = useCallback(async () => {
    setVisible(false);
    const shopify = window.shopify;

    if (!shopify?.reviews) {
      report('unavailable');
      shopify?.toast?.show(t.reviewPrompt.unavailable);
      return;
    }

    try {
      const result = await shopify.reviews.request();
      if (result.success) {
        report('requested');
        return;
      }
      // Shopify declined to show it (its own rate limits, a merchant who already
      // reviewed, mobile, and so on). Say so plainly instead of failing silently.
      console.log(`[Review] modal not displayed: ${result.code}: ${result.message}`);
      report('unavailable');
      shopify.toast?.show(t.reviewPrompt.unavailable);
    } catch (error) {
      console.warn('[Review] request failed:', error);
      report('unavailable');
      shopify.toast?.show(t.reviewPrompt.unavailable);
    }
  }, [report, t]);

  const maybeLater = useCallback(() => {
    setVisible(false);
    report('snoozed');
  }, [report]);

  const dismiss = useCallback(() => {
    setVisible(false);
    report('dismissed');
  }, [report]);

  return { visible, leaveReview, maybeLater, dismiss };
}
